import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Building2, Wrench, AlertTriangle, CheckCircle2,
  DoorOpen, Hammer, Clock, ArrowUpDown, UserCheck,
} from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar,
  XAxis, YAxis, CartesianGrid,
} from "recharts";

const COLORS = ["#10b981", "#0ea5e9", "#f59e0b", "#ef4444", "#8b5cf6", "#6366f1"];
const ROOM_STATUS_COLORS = {
  available: "bg-emerald-50 text-emerald-700",
  occupied: "bg-sky-50 text-sky-700",
  reserved: "bg-amber-50 text-amber-700",
  maintenance: "bg-red-50 text-red-700",
};
const TICKET_STATUS_COLORS = {
  open: "bg-red-50 text-red-700",
  assigned: "bg-blue-50 text-blue-700",
  in_progress: "bg-amber-50 text-amber-700",
  awaiting_tenant: "bg-violet-50 text-violet-700",
  resolved: "bg-emerald-50 text-emerald-700",
  closed: "bg-slate-100 text-slate-600",
  escalated: "bg-rose-50 text-rose-700",
};
const SEVERITY_COLORS = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-amber-50 text-amber-700",
  high: "bg-orange-50 text-orange-700",
  urgent: "bg-red-50 text-red-700",
};

function KPI({ label, value, sub, icon: Icon, color = "text-sky-600" }) {
  return (
    <Card className="border-slate-200" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{label}</span>
          <Icon className={`h-4 w-4 ${color}`} />
        </div>
        <div className="font-heading text-2xl font-bold text-slate-900">{value}</div>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function PropertyManagementDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [ticketFilter, setTicketFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [roomStatusDialog, setRoomStatusDialog] = useState(null);
  const [newRoomStatus, setNewRoomStatus] = useState("");
  const [ticketDialog, setTicketDialog] = useState(null);
  const [ticketAction, setTicketAction] = useState("status");
  const [newTicketStatus, setNewTicketStatus] = useState("");
  const [ticketNotes, setTicketNotes] = useState("");
  const [assignTo, setAssignTo] = useState("");

  const fetchDashboard = useCallback(async () => {
    try {
      const { data } = await api.get("/property/dashboard");
      setDashboard(data);
    } catch { /* ignore */ }
  }, []);

  const fetchRooms = useCallback(async () => {
    try {
      const { data } = await api.get("/property/rooms");
      setRooms(data);
    } catch { /* ignore */ }
  }, []);

  const fetchTickets = useCallback(async (status, severity) => {
    try {
      let url = "/property/maintenance";
      const params = [];
      if (status && status !== "all") params.push(`status=${status}`);
      if (severity && severity !== "all") params.push(`severity=${severity}`);
      if (params.length) url += "?" + params.join("&");
      const { data } = await api.get(url);
      setTickets(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    Promise.all([fetchDashboard(), fetchRooms(), fetchTickets()])
      .finally(() => setLoading(false));
  }, [fetchDashboard, fetchRooms, fetchTickets]);

  const handleRoomStatusUpdate = async () => {
    if (!roomStatusDialog || !newRoomStatus) return;
    try {
      await api.put(`/property/rooms/${roomStatusDialog.id}/status`, { status: newRoomStatus });
      toast.success(`Room status updated to ${newRoomStatus}`);
      setRoomStatusDialog(null);
      fetchRooms();
      fetchDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update room");
    }
  };

  const handleTicketStatusUpdate = async () => {
    if (!ticketDialog || !newTicketStatus) return;
    try {
      await api.put(`/property/maintenance/${ticketDialog.id}/status`, { status: newTicketStatus, notes: ticketNotes });
      toast.success(`Ticket status updated to ${newTicketStatus}`);
      setTicketDialog(null);
      setTicketNotes("");
      fetchTickets(ticketFilter, severityFilter);
      fetchDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update ticket");
    }
  };

  const handleTicketAssign = async () => {
    if (!ticketDialog || !assignTo) return;
    try {
      await api.put(`/property/maintenance/${ticketDialog.id}/assign`, { assigned_to: assignTo });
      toast.success("Ticket assigned");
      setTicketDialog(null);
      setAssignTo("");
      fetchTickets(ticketFilter, severityFilter);
      fetchDashboard();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign");
    }
  };

  const applyTicketFilters = (status, severity) => {
    setTicketFilter(status);
    setSeverityFilter(severity);
    fetchTickets(status, severity);
  };

  if (loading) {
    return (
      <DashboardLayout title="Property Management">
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </DashboardLayout>
    );
  }

  const d = dashboard || {};
  const roomsByStatus = (d.rooms?.by_status || []).map((s) => ({ name: s._id || "unknown", value: s.count }));
  const ticketsByCategory = (d.maintenance?.by_category || []).map((c) => ({ name: c._id || "unknown", value: c.count }));
  const floorData = (d.rooms?.by_floor || []).sort((a, b) => (a._id || 0) - (b._id || 0)).map((f) => ({
    name: `Floor ${f._id || "?"}`, available: f.available || 0, occupied: f.occupied || 0, maintenance: f.maint || 0,
  }));

  const totalAvailable = roomsByStatus.find((r) => r.name === "available")?.value || 0;
  const totalOccupied = roomsByStatus.find((r) => r.name === "occupied")?.value || 0;

  return (
    <DashboardLayout title="Property Management">
      <div className="space-y-6" data-testid="property-dashboard">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100" data-testid="property-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="rooms" data-testid="tab-rooms">Rooms</TabsTrigger>
            <TabsTrigger value="maintenance" data-testid="tab-maintenance">Maintenance</TabsTrigger>
          </TabsList>

          {/* ============ OVERVIEW ============ */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <KPI label="Total Rooms" value={d.rooms?.total || 0} icon={Building2} color="text-sky-600" />
              <KPI label="Available" value={totalAvailable} icon={DoorOpen} color="text-emerald-600" />
              <KPI label="Occupied" value={totalOccupied} icon={CheckCircle2} color="text-blue-600" />
              <KPI label="Open Tickets" value={d.maintenance?.open || 0} icon={Wrench} color="text-amber-600" />
              <KPI label="Urgent" value={d.maintenance?.urgent || 0} icon={AlertTriangle} color="text-red-500" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Rooms by Status */}
              <Card className="border-slate-200">
                <CardHeader><CardTitle className="font-heading text-base font-bold">Rooms by Status</CardTitle></CardHeader>
                <CardContent>
                  {roomsByStatus.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={roomsByStatus} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                          {roomsByStatus.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="text-center text-slate-400 py-12 text-sm">No room data</p>}
                </CardContent>
              </Card>

              {/* Floor Breakdown */}
              <Card className="border-slate-200 lg:col-span-2">
                <CardHeader><CardTitle className="font-heading text-base font-bold">Floor Breakdown</CardTitle></CardHeader>
                <CardContent>
                  {floorData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={floorData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="available" fill="#10b981" name="Available" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="occupied" fill="#0ea5e9" name="Occupied" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="maintenance" fill="#ef4444" name="Maintenance" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-center text-slate-400 py-12 text-sm">No floor data</p>}
                </CardContent>
              </Card>
            </div>

            {/* Tickets by Category */}
            {ticketsByCategory.length > 0 && (
              <Card className="border-slate-200">
                <CardHeader><CardTitle className="font-heading text-base font-bold">Open Tickets by Category</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={ticketsByCategory} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={120} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Recent Tickets */}
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="font-heading text-base font-bold">Recent Maintenance Requests</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-bold uppercase">Tenant</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Room</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Category</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Severity</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Status</TableHead>
                      <TableHead className="text-xs font-bold uppercase">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(d.recent_tickets || []).map((t) => (
                      <TableRow key={t.id || t._id}>
                        <TableCell className="text-sm font-medium">{t.user_name || "—"}</TableCell>
                        <TableCell className="text-sm">{t.room_number || "—"}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[11px]">{t.category || "—"}</Badge></TableCell>
                        <TableCell><Badge className={SEVERITY_COLORS[t.severity] || "bg-slate-100"}>{t.severity || "—"}</Badge></TableCell>
                        <TableCell><Badge className={TICKET_STATUS_COLORS[t.status] || "bg-slate-100"}>{t.status?.replace(/_/g, " ")}</Badge></TableCell>
                        <TableCell className="text-xs text-slate-400">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</TableCell>
                      </TableRow>
                    ))}
                    {(d.recent_tickets || []).length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No recent tickets</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ ROOMS ============ */}
          <TabsContent value="rooms" className="mt-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-base font-bold flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-sky-500" /> Room Inventory
                  <Badge variant="outline" className="ml-2 font-mono">{rooms.length} rooms</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-bold uppercase">Room #</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Floor</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Category</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Capacity</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Price</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Status</TableHead>
                        <TableHead className="text-xs font-bold uppercase text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rooms.map((r) => (
                        <TableRow key={r.id || r._id} data-testid={`room-row-${r.id || r._id}`}>
                          <TableCell className="font-mono font-bold text-sm">{r.number}</TableCell>
                          <TableCell className="text-sm">{r.floor}</TableCell>
                          <TableCell><Badge variant="outline" className="text-[11px] capitalize">{r.category}</Badge></TableCell>
                          <TableCell className="text-sm text-center">{r.capacity}</TableCell>
                          <TableCell className="font-mono text-sm">${r.price}/mo</TableCell>
                          <TableCell><Badge className={ROOM_STATUS_COLORS[r.status] || "bg-slate-100"}>{r.status}</Badge></TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              data-testid={`update-room-${r.id || r._id}`}
                              onClick={() => { setRoomStatusDialog({ id: r.id || r._id, number: r.number, current: r.status }); setNewRoomStatus(r.status); }}
                            >
                              <ArrowUpDown className="h-3 w-3 mr-1" /> Status
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {rooms.length === 0 && (
                        <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No rooms</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Room Status Dialog */}
            <Dialog open={!!roomStatusDialog} onOpenChange={(o) => !o && setRoomStatusDialog(null)}>
              <DialogContent data-testid="room-status-dialog">
                <DialogHeader>
                  <DialogTitle>Update Room {roomStatusDialog?.number}</DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <Select value={newRoomStatus} onValueChange={setNewRoomStatus}>
                    <SelectTrigger data-testid="room-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="occupied">Occupied</SelectItem>
                      <SelectItem value="reserved">Reserved</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setRoomStatusDialog(null)}>Cancel</Button>
                  <Button onClick={handleRoomStatusUpdate} disabled={newRoomStatus === roomStatusDialog?.current} data-testid="confirm-room-status">
                    Update
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* ============ MAINTENANCE ============ */}
          <TabsContent value="maintenance" className="mt-6 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-slate-600">Status:</span>
              <Select value={ticketFilter} onValueChange={(v) => applyTicketFilters(v, severityFilter)}>
                <SelectTrigger className="w-36" data-testid="ticket-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="escalated">Escalated</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm font-medium text-slate-600">Severity:</span>
              <Select value={severityFilter} onValueChange={(v) => applyTicketFilters(ticketFilter, v)}>
                <SelectTrigger className="w-32" data-testid="ticket-severity-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
              <Badge variant="outline" className="font-mono">{tickets.length} tickets</Badge>
            </div>

            <Card className="border-slate-200">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-bold uppercase">Tenant</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Room</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Category</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Severity</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Status</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Assigned To</TableHead>
                        <TableHead className="text-xs font-bold uppercase">Date</TableHead>
                        <TableHead className="text-xs font-bold uppercase text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tickets.map((t) => (
                        <TableRow key={t.id || t._id} data-testid={`ticket-row-${t.id || t._id}`}>
                          <TableCell className="text-sm font-medium">{t.user_name || "—"}</TableCell>
                          <TableCell className="text-sm">{t.room_number || "—"}</TableCell>
                          <TableCell><Badge variant="outline" className="text-[11px]">{t.category || "—"}</Badge></TableCell>
                          <TableCell><Badge className={SEVERITY_COLORS[t.severity] || "bg-slate-100"}>{t.severity}</Badge></TableCell>
                          <TableCell><Badge className={TICKET_STATUS_COLORS[t.status] || "bg-slate-100"}>{t.status?.replace(/_/g, " ")}</Badge></TableCell>
                          <TableCell className="text-sm text-slate-600">{t.assigned_to || "Unassigned"}</TableCell>
                          <TableCell className="text-xs text-slate-400">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              data-testid={`update-ticket-${t.id || t._id}`}
                              onClick={() => {
                                setTicketDialog({ id: t.id || t._id, current: t.status });
                                setTicketAction("status");
                                setNewTicketStatus(t.status);
                                setTicketNotes("");
                              }}
                            >
                              <Hammer className="h-3 w-3 mr-1" /> Status
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              data-testid={`assign-ticket-${t.id || t._id}`}
                              onClick={() => {
                                setTicketDialog({ id: t.id || t._id, current: t.status });
                                setTicketAction("assign");
                                setAssignTo(t.assigned_to || "");
                              }}
                            >
                              <UserCheck className="h-3 w-3 mr-1" /> Assign
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {tickets.length === 0 && (
                        <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">No tickets found</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Ticket Update Dialog */}
            <Dialog open={!!ticketDialog} onOpenChange={(o) => !o && setTicketDialog(null)}>
              <DialogContent data-testid="ticket-dialog">
                <DialogHeader>
                  <DialogTitle>{ticketAction === "assign" ? "Assign Ticket" : "Update Ticket Status"}</DialogTitle>
                </DialogHeader>
                {ticketAction === "status" ? (
                  <div className="space-y-4 py-2">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">New Status</label>
                      <Select value={newTicketStatus} onValueChange={setNewTicketStatus}>
                        <SelectTrigger data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="assigned">Assigned</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="awaiting_tenant">Awaiting Tenant</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                          <SelectItem value="escalated">Escalated</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Notes (optional)</label>
                      <Textarea
                        value={ticketNotes}
                        onChange={(e) => setTicketNotes(e.target.value)}
                        placeholder="Add notes..."
                        data-testid="ticket-notes"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="py-4">
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Assign To</label>
                    <Input
                      value={assignTo}
                      onChange={(e) => setAssignTo(e.target.value)}
                      placeholder="Technician / staff name"
                      data-testid="assign-to-input"
                    />
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setTicketDialog(null)}>Cancel</Button>
                  {ticketAction === "status" ? (
                    <Button onClick={handleTicketStatusUpdate} disabled={newTicketStatus === ticketDialog?.current} data-testid="confirm-ticket-status">
                      Update
                    </Button>
                  ) : (
                    <Button onClick={handleTicketAssign} disabled={!assignTo.trim()} data-testid="confirm-ticket-assign">
                      Assign
                    </Button>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
